<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Hero Section -->
    <section class="relative h-[480px] overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-fuchsia-600">
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>

      <div class="absolute inset-0 flex items-center">
        <div class="w-full px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 pt-[80px] md:pt-[120px]">
          <div class="flex flex-col md:flex-row items-start md:items-center gap-8 max-w-6xl mx-auto">
            <div class="text-white md:w-1/2">
              <h1 class="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4">
                Voyagez<br>
                <span class="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-400">
                  connecté
                </span>
              </h1>
              <p class="text-lg lg:text-xl text-white/80">
                eSim pour voyager sans changer de carte SIM
              </p>
            </div>

            <!-- Selected Country Display -->
            <div v-if="selectedCountry" class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <div class="flex items-center gap-4 mb-4">
                  <img
                    v-if="selectedCountry.localLogoUrl"
                    :src="selectedCountry.localLogoUrl"
                    :alt="selectedCountry.countryName"
                    class="w-12 h-12 object-contain"
                  >
                  <span v-else class="text-4xl">{{ getCountryFlag() }}</span>
                  <div class="text-white">
                    <p class="font-bold text-lg">{{ getCountryName() }}</p>
                    <p class="text-sm text-white/70">{{ getCountryCurrency() }}</p>
                  </div>
                </div>
                <p class="text-white/80 text-sm">
                  Les forfaits eSim disponibles pour ce pays s'affichent ci-dessous
                </p>
              </div>
            </div>

            <!-- No Country Selected State -->
            <div v-else class="w-full md:w-[320px] md:ml-auto">
              <div class="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl">
                <p class="text-white font-semibold mb-2">Sélectionnez un pays</p>
                <p class="text-white/70 text-sm">
                  Utilisez le sélecteur de pays dans le header pour commencer
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="flex-1 py-12 bg-white">
      <div class="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">

        <!-- No Country Selected State -->
        <div v-if="!selectedCountry" class="text-center py-20">
          <div class="text-7xl mb-6">🌍</div>
          <h3 class="text-2xl font-bold text-gray-900 mb-3">
            Sélectionnez un pays
          </h3>
          <p class="text-gray-600 text-lg">
            Utilisez le sélecteur de pays dans le header pour voir les forfaits eSim disponibles.
          </p>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="text-center py-20">
          <div class="inline-block w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="mt-6 text-gray-600 text-lg">Chargement des forfaits...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl text-center max-w-lg mx-auto mt-8">
          {{ error }}
        </div>

        <!-- Packages Section -->
        <template v-else>
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
            Forfaits disponibles
          </h2>

          <!-- Packages Grid -->
          <div v-if="packages.length > 0" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div
              v-for="pkg in packages"
              :key="pkg.packageCode"
              @click="selectPackage(pkg)"
              class="package-card group"
            >
              <div class="package-card-inner">
                <div class="package-header">
                  <div class="package-data">
                    {{ formatData(pkg.volume) }}
                  </div>
                  <div class="package-duration">{{ pkg.duration }} jours</div>
                </div>
                <div class="package-name">{{ pkg.name }}</div>
                <div class="package-details">
                  <span class="package-speed">{{ pkg.speed }}</span>
                </div>
                <div class="package-price">
                  {{ formatPrice(pkg.price) }}
                </div>
                <button class="package-button">
                  Choisir
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-20">
            <div class="text-7xl mb-6">📭</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">
              Aucun forfait disponible
            </h3>
            <p class="text-gray-600 text-lg mb-6">
              Aucun forfait eSim disponible pour ce pays.
            </p>
            <p class="text-gray-500">
              Sélectionnez un autre pays pour voir les forfaits disponibles.
            </p>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores } = proxy;

const loading = ref(true);
const error = ref(null);
const packages = ref([]);

// Selected country from global store
const selectedCountry = computed(() => $stores.global.state.selectedCountry);

// Map country code to esim location code
const getEsimLocationCode = (countryCode) => {
  const mapping = {
    'BJ': 'BJ', 'TG': 'TG', 'CI': 'CI', 'SN': 'SN', 'ML': 'ML',
    'FR': 'FR', 'BE': 'BE', 'CH': 'CH', 'CM': 'CM', 'CD': 'CD',
    'NE': 'NE', 'BF': 'BF', 'GN': 'GN', 'GA': 'GA', 'CG': 'CG',
    'CF': 'CF', 'TD': 'TD', 'MG': 'MG', 'RW': 'RW', 'BI': 'BI',
    'DJ': 'DJ', 'ER': 'ER', 'ET': 'ET', 'KE': 'KE', 'MG': 'MG',
    'MW': 'MW', 'MU': 'MU', 'MZ': 'MZ', 'NA': 'NA', 'RW': 'RW',
    'SC': 'SC', 'TZ': 'TZ', 'UG': 'UG', 'ZM': 'ZM', 'ZW': 'ZW',
    'AO': 'AO', 'NA': 'NA', 'ZA': 'ZA', 'EG': 'EG', 'MA': 'MA',
    'TN': 'TN', 'DZ': 'DZ', 'LY': 'LY', 'NG': 'NG', 'GH': 'GH',
    'CI': 'CI', 'SN': 'SN', 'ML': 'ML', 'BF': 'BF', 'NE': 'NE',
    'TD': 'TD', 'CF': 'CF', 'CD': 'CD', 'CG': 'CG', 'GA': 'GA',
    'CM': 'CM', 'LR': 'LR', 'SL': 'SL', 'GN': 'GN', 'GW': 'GW',
    'GM': 'GM', 'MR': 'MR', 'SO': 'SO', 'SD': 'SD', 'ET': 'ET',
    'KE': 'KE', 'UG': 'UG', 'TZ': 'TZ', 'RW': 'RW', 'BI': 'BI',
    'MW': 'MW', 'ZM': 'ZM', 'MZ': 'MZ', 'ZW': 'ZW', 'BW': 'BW',
    'NA': 'NA', 'SZ': 'SZ', 'LS': 'LS', 'ZA': 'ZA'
  };
  return mapping[countryCode] || countryCode;
};

// Watch for country changes and refetch packages
watch(() => $stores.global.state.selectedCountry, () => {
  if ($stores.global.state.selectedCountry) {
    fetchPackages();
  }
});

onMounted(async () => {
  if ($stores.global.state.selectedCountry) {
    await fetchPackages();
  } else {
    loading.value = false;
  }
});

const fetchPackages = async () => {
  const country = $stores.global.state.selectedCountry;
  if (!country) {
    packages.value = [];
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const { bus } = await import('@core/events/eventSystem');
    const constants = await import('../constants');

    const locationCode = getEsimLocationCode(country.countryCode);

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        packages.value = data || [];
        loading.value = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        packages.value = [];
        loading.value = false;
        // Don't show error for countries without packages
        reject(err);
      };

      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);

      bus.emit(constants.EVENTS.ESIM.FETCH_PACKAGES, locationCode);
    });
  } catch (e) {
    packages.value = [];
    loading.value = false;
  }
};

const getCountryFlag = () => {
  if (!selectedCountry.value) return '🌍';
  const countryCode = selectedCountry.value.countryCode;
  const flags = {
    'BJ': '🇧🇯', 'TG': '🇹🇬', 'CI': '🇨🇮', 'SN': '🇸🇳', 'ML': '🇲🇱',
    'FR': '🇫🇷', 'BE': '🇧🇪', 'CH': '🇨🇭', 'CM': '🇨🇲', 'CD': '🇨🇩',
    'NE': '🇳🇪', 'BF': '🇧🇫', 'GN': '🇬🇳', 'GA': '🇬🇦', 'CG': '🇨🇬',
    'CF': '🇨🇫', 'TD': '🇹🇩', 'MG': '🇲🇬', 'RW': '🇷🇼', 'BI': '🇧🇮'
  };
  return flags[countryCode] || '🌍';
};

const getCountryName = () => {
  return selectedCountry.value?.countryName || 'Sélectionner un pays';
};

const getCountryCurrency = () => {
  return selectedCountry.value ? `${selectedCountry.value.currency}` : '';
};

const selectPackage = async (pkg) => {
  const country = $stores.global.state.selectedCountry;

  // Navigate to product page with slug format: packageCode-countryCode
  const slug = `${pkg.packageCode}-${country.countryCode.toLowerCase()}`;

  router.push({
    name: 'esim-product',
    params: { slug }
  });
};

const formatData = (volume) => {
  if (!volume) return 'Illimité';
  // If already formatted as string (e.g., "1GB", "500MB"), return as-is
  if (typeof volume === 'string') return volume;
  // If numeric, convert from bytes to GB/MB
  const gb = volume / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Math.round(gb)}GB`;
  const mb = volume / (1024 * 1024);
  return `${Math.round(mb)}MB`;
};

const formatPrice = (price) => {
  if (!price) return '---';
  // API returns prices in cents, divide by 100
  const priceInUnits = price / 100;
  // Get currency from selected country, default to XOF
  const currency = selectedCountry.value?.currency || 'XOF';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(priceInUnits);
};
</script>

<style scoped>
/* Package Card */
.package-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.package-card-inner {
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.package-card:hover .package-card-inner {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: rgba(1, 243, 122, 0.3);
  transform: translateY(-4px);
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.package-data {
  font-size: 28px;
  font-weight: 700;
  color: #01F37A;
}

.package-duration {
  font-size: 14px;
  color: #666;
  background: #f0fdf4;
  padding: 4px 12px;
  border-radius: 12px;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
  flex-grow: 1;
}

.package-details {
  margin-bottom: 16px;
}

.package-speed {
  font-size: 13px;
  color: #666;
}

.package-price {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.package-button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(to right, #01F37A, #00D9FF);
  color: white;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.package-button:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(1, 243, 122, 0.3);
}
</style>
